/*
 * FindMySizeDrawer — Right-side slide-in drawer that walks the shopper
 * through a 4-step size finder (basic info → stomach → torso → thigh),
 * then returns a recommended size (S–XXL) with SLIMMER / IDEAL / LOOSER
 * fit variants and a direct add-to-cart CTA.
 *
 * Recommendation is a deterministic heuristic based on weight, height,
 * stomach shape, torso shape and thigh shape — no external API.
 */

import { useEffect, useMemo, useState } from "react";
import { X, ChevronLeft, Camera } from "lucide-react";

const SIZES = ["S", "M", "L", "XL", "XXL"];

// ─────────── Body-shape illustrations (Represent-style human silhouettes) ───────────
// Matches user-supplied reference screenshots: outlined body silhouette on
// white with a dashed emphasis curve showing the shape variation, and grey
// trousers on the lower half. Neutral palette (no skin tone) to feel neutral.
const OUT = "#4b4b4b";       // silhouette outline
const OUT_STRONG = "#1a1a1a"; // belt / accent
const TROUSER = "#8a8a8a";   // grey trousers fill
const DASH_STROKE = "#1a1a1a";

// StomachIcon — right-facing side profile of a slim figure. Only the belly
// dashed curve differs per variant (FLAT / MEDIUM / ROUND).
const StomachIcon = ({ variant }) => {
  const dashPath = {
    // Almost vertical: barely any belly
    flat: "M56 60 Q58 78 54 92",
    // Gentle bulge
    medium: "M56 60 Q66 78 54 92",
    // Pronounced bulge
    round: "M56 60 Q76 78 54 92",
  }[variant];
  return (
    <svg viewBox="0 0 110 160" className="w-full h-full" fill="none" stroke={OUT} strokeWidth="1.2" strokeLinejoin="round" strokeLinecap="round">
      {/* head profile (right-facing) */}
      <path d="M55 12 C46 12 41 18 41 26 C41 32 43 36 46 38 L46 44 L52 46 L58 44 L58 40 C62 40 64 36 64 32 C64 24 62 18 55 12 Z" />
      {/* ear hint */}
      <circle cx="49" cy="30" r="1.3" fill={OUT} stroke="none" />
      {/* neck */}
      <path d="M50 46 L50 54 L60 54 L60 46" />
      {/* torso back edge (left, straight) */}
      <path d="M46 54 L46 100" />
      {/* torso front edge (right) — gentle chest curve then vertical */}
      <path d="M60 54 Q62 62 60 92 L60 100" />
      {/* arm hanging behind slightly */}
      <path d="M47 56 Q42 82 44 98" />
      {/* belt */}
      <path d="M42 100 L62 100 L62 108 L42 108 Z" stroke={OUT_STRONG} strokeWidth="1" fill={OUT_STRONG} />
      {/* trousers (grey, side view) */}
      <path d="M42 108 L44 156 L52 156 L54 108 Z" fill={TROUSER} stroke={OUT} />
      <path d="M54 108 L56 156 L62 156 L62 108 Z" fill={TROUSER} stroke={OUT} />
      {/* dashed belly emphasis */}
      <path d={dashPath} stroke={DASH_STROKE} strokeWidth="1" strokeDasharray="3 3" opacity="0.9" />
    </svg>
  );
};

// TorsoIcon — front view of upper body. Shoulder + torso width varies per variant.
const TorsoIcon = ({ variant }) => {
  const shW = { slim: 20, medium: 26, muscular: 34 }[variant];
  const cx = 55;
  return (
    <svg viewBox="0 0 110 160" className="w-full h-full" fill="none" stroke={OUT} strokeWidth="1.2" strokeLinejoin="round" strokeLinecap="round">
      {/* head */}
      <ellipse cx={cx} cy="22" rx="10" ry="12" />
      {/* jawline hint */}
      <path d="M48 30 Q55 34 62 30" opacity="0.55" />
      {/* neck */}
      <path d={`M${cx-4} 34 L${cx-4} 44 L${cx+4} 44 L${cx+4} 34`} />
      {/* shoulders/torso outline */}
      <path d={`M${cx-shW} 46 Q${cx} 42 ${cx+shW} 46 Q${cx+shW-3} 70 ${cx+shW-8} 98 L${cx-shW+8} 98 Q${cx-shW+3} 70 ${cx-shW} 46 Z`} />
      {/* subtle chest split */}
      <path d={`M${cx} 46 L${cx} 96`} opacity="0.35" />
      {/* pectoral hint for muscular */}
      {variant !== "slim" && (
        <>
          <path d={`M${cx-shW+6} 60 Q${cx-4} 68 ${cx-1} 62`} opacity="0.4" />
          <path d={`M${cx+shW-6} 60 Q${cx+4} 68 ${cx+1} 62`} opacity="0.4" />
        </>
      )}
      {/* arms — hanging down along the outer edge */}
      <path d={`M${cx-shW+1} 46 Q${cx-shW-3} 76 ${cx-shW+2} 100`} />
      <path d={`M${cx+shW-1} 46 Q${cx+shW+3} 76 ${cx+shW-2} 100`} />
      {/* belt */}
      <rect x={cx-shW+6} y="98" width={(shW*2)-12} height="8" fill={OUT_STRONG} stroke="none" />
      {/* trousers */}
      <path d={`M${cx-shW+6} 106 L${cx-shW+8} 156 L${cx-2} 156 L${cx-1} 106 Z`} fill={TROUSER} stroke={OUT} />
      <path d={`M${cx+1} 106 L${cx+2} 156 L${cx+shW-8} 156 L${cx+shW-6} 106 Z`} fill={TROUSER} stroke={OUT} />
    </svg>
  );
};

// ThighIcon — waist-down view. Thigh width varies per variant. Outer dashed
// curve emphasises the outward bulge.
const ThighIcon = ({ variant }) => {
  const thighW = { thin: 12, medium: 16, large: 22 }[variant];
  const bulge = { thin: 2, medium: 5, large: 10 }[variant];
  const cx = 55;
  const gap = 3;
  return (
    <svg viewBox="0 0 110 160" className="w-full h-full" fill="none" stroke={OUT} strokeWidth="1.2" strokeLinejoin="round" strokeLinecap="round">
      {/* faint upper torso hint */}
      <path d={`M${cx-16} 4 Q${cx} 0 ${cx+16} 4 L${cx+14} 40 L${cx-14} 40 Z`} opacity="0.35" />
      {/* belt */}
      <rect x={cx-20} y="40" width="40" height="8" fill={OUT_STRONG} stroke="none" />
      {/* left thigh (grey trouser) */}
      <path d={`M${cx-thighW-gap} 48 L${cx-gap} 48 L${cx-gap} 150 L${cx-thighW-gap} 150 Z`} fill={TROUSER} stroke={OUT} />
      {/* right thigh */}
      <path d={`M${cx+gap} 48 L${cx+thighW+gap} 48 L${cx+thighW+gap} 150 L${cx+gap} 150 Z`} fill={TROUSER} stroke={OUT} />
      {/* outer curve emphasis — dashed */}
      <path d={`M${cx-thighW-gap} 60 Q${cx-thighW-gap-bulge} 100 ${cx-thighW-gap} 145`} stroke={DASH_STROKE} strokeDasharray="3 3" opacity="0.9" />
      <path d={`M${cx+thighW+gap} 60 Q${cx+thighW+gap+bulge} 100 ${cx+thighW+gap} 145`} stroke={DASH_STROKE} strokeDasharray="3 3" opacity="0.9" />
    </svg>
  );
};

const STOMACH_OPTS = [
  { id: "flat", label: "FLAT" },
  { id: "medium", label: "MEDIUM" },
  { id: "round", label: "ROUND" },
];
const TORSO_OPTS = [
  { id: "slim", label: "SLIM" },
  { id: "medium", label: "MEDIUM" },
  { id: "muscular", label: "MUSCULAR" },
];
const THIGH_OPTS = [
  { id: "thin", label: "THIN" },
  { id: "medium", label: "MEDIUM" },
  { id: "large", label: "LARGE" },
];

// ─────────── Size recommendation heuristic ───────────
function recommendSize({ heightCm, weightKg, stomach, torso, thigh, shoppingFor }) {
  // Base by weight (men chart approximation for jackets)
  let idx;
  if (weightKg < 65) idx = 0; // S
  else if (weightKg < 75) idx = 1; // M
  else if (weightKg < 88) idx = 2; // L
  else if (weightKg < 102) idx = 3; // XL
  else idx = 4; // XXL

  // Height modifier: very tall +0.5, very short -0.5 (rounded)
  if (heightCm >= 190) idx += 0.5;
  else if (heightCm < 165) idx -= 0.5;

  // Shape modifiers
  if (torso === "muscular") idx += 1;
  else if (torso === "slim") idx -= 1;

  if (stomach === "round") idx += 1;
  else if (stomach === "flat") idx -= 0;

  if (thigh === "large") idx += 0.5;

  // Women shopping = one size down for jackets
  if (shoppingFor === "women") idx -= 1;

  idx = Math.round(idx);
  idx = Math.max(0, Math.min(SIZES.length - 1, idx));
  return SIZES[idx];
}

function feetInchesToCm(ft, inch) {
  return Math.round((parseInt(ft || 0, 10) * 12 + parseInt(inch || 0, 10)) * 2.54);
}
function lbsToKg(lbs) {
  return Math.round(parseFloat(lbs || 0) * 0.45359);
}

// ─────────── Main drawer component ───────────
export default function FindMySizeDrawer({ open, onClose, availableSizes = SIZES, onPick }) {
  const [step, setStep] = useState(1); // 1..4, 5=result
  const [heightUnit, setHeightUnit] = useState("cm"); // cm | feet
  const [heightCm, setHeightCm] = useState("");
  const [heightFt, setHeightFt] = useState("");
  const [heightIn, setHeightIn] = useState("");
  const [weightUnit, setWeightUnit] = useState("kg"); // kg | lbs
  const [weight, setWeight] = useState("");
  const [age, setAge] = useState("");
  const [shoppingFor, setShoppingFor] = useState("men");
  const [stomach, setStomach] = useState(null);
  const [torso, setTorso] = useState(null);
  const [thigh, setThigh] = useState(null);
  const [fitVariant, setFitVariant] = useState("ideal"); // slimmer | ideal | looser

  // Body-scroll lock while open + FULL wizard reset on each fresh open
  useEffect(() => {
    if (open) {
      setStep(1);
      setFitVariant("ideal");
      setHeightCm("");
      setHeightFt("");
      setHeightIn("");
      setWeight("");
      setAge("");
      setStomach(null);
      setTorso(null);
      setThigh(null);
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  const resolvedHeightCm = heightUnit === "cm" ? parseInt(heightCm || 0, 10) : feetInchesToCm(heightFt, heightIn);
  const resolvedWeightKg = weightUnit === "kg" ? parseFloat(weight || 0) : lbsToKg(weight);

  const idealSize = useMemo(() => {
    if (step < 5) return null;
    return recommendSize({
      heightCm: resolvedHeightCm,
      weightKg: resolvedWeightKg,
      stomach,
      torso,
      thigh,
      shoppingFor,
    });
  }, [step, resolvedHeightCm, resolvedWeightKg, stomach, torso, thigh, shoppingFor]);

  const shownSize = useMemo(() => {
    if (!idealSize) return null;
    const i = SIZES.indexOf(idealSize);
    if (fitVariant === "slimmer") return SIZES[Math.max(0, i - 1)];
    if (fitVariant === "looser") return SIZES[Math.min(SIZES.length - 1, i + 1)];
    return idealSize;
  }, [idealSize, fitVariant]);

  const step1Valid = resolvedHeightCm > 100 && resolvedWeightKg > 30 && parseInt(age || 0, 10) >= 13;

  const goBack = () => {
    if (step === 5) setStep(4);
    else if (step > 1) setStep(step - 1);
  };
  const goNext = () => setStep((s) => s + 1);
  const startOver = () => {
    setStep(1);
    setStomach(null);
    setTorso(null);
    setThigh(null);
    setFitVariant("ideal");
  };
  const addPickedToCart = () => {
    onPick?.(shownSize);
    onClose();
  };

  if (!open) return null;

  return (
    <div data-testid="find-my-size-drawer" className="fixed inset-0 z-[9997]" role="dialog" aria-modal="true" aria-labelledby="fms-title">
      {/* backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} data-testid="fms-backdrop" />
      {/* right sheet */}
      <div className="absolute top-0 right-0 h-full w-full sm:w-[520px] bg-white text-black shadow-2xl overflow-y-auto animate-[slideInRight_400ms_ease-out]">
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-6 py-4 border-b border-black/10">
          {step > 1 && step < 5 ? (
            <button onClick={goBack} data-testid="fms-back" aria-label="Back" className="p-1 -ml-1">
              <ChevronLeft size={20} strokeWidth={1.5} />
            </button>
          ) : (
            <span />
          )}
          <button onClick={onClose} data-testid="fms-close" aria-label="Close" className="p-1 -mr-1">
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>

        <div className="px-6 md:px-8 pb-10 pt-6">
          {step === 1 && (
            <Step1
              heightUnit={heightUnit} setHeightUnit={setHeightUnit}
              heightCm={heightCm} setHeightCm={setHeightCm}
              heightFt={heightFt} setHeightFt={setHeightFt}
              heightIn={heightIn} setHeightIn={setHeightIn}
              weightUnit={weightUnit} setWeightUnit={setWeightUnit}
              weight={weight} setWeight={setWeight}
              age={age} setAge={setAge}
              shoppingFor={shoppingFor} setShoppingFor={setShoppingFor}
              onContinue={goNext}
              valid={step1Valid}
            />
          )}
          {step === 2 && (
            <ShapeStep
              testPrefix="stomach"
              title="Select your stomach shape"
              subtitle="This helps us understand your body shape."
              options={STOMACH_OPTS}
              value={stomach}
              onChange={setStomach}
              onContinue={goNext}
              dotIndex={0}
              iconRender={(id) => <StomachIcon variant={id} />}
            />
          )}
          {step === 3 && (
            <ShapeStep
              testPrefix="torso"
              title="Select your torso shape"
              subtitle="This helps us understand your body shape."
              options={TORSO_OPTS}
              value={torso}
              onChange={setTorso}
              onContinue={goNext}
              dotIndex={1}
              iconRender={(id) => <TorsoIcon variant={id} />}
            />
          )}
          {step === 4 && (
            <ShapeStep
              testPrefix="thigh"
              title="Select your thigh shape"
              subtitle="This helps us understand your body shape."
              options={THIGH_OPTS}
              value={thigh}
              onChange={setThigh}
              onContinue={goNext}
              dotIndex={2}
              iconRender={(id) => <ThighIcon variant={id} />}
              ctaLabel="Find my ideal size"
            />
          )}
          {step === 5 && (
            <ResultStep
              shownSize={shownSize}
              idealSize={idealSize}
              fitVariant={fitVariant}
              setFitVariant={setFitVariant}
              onAdd={addPickedToCart}
              onStartOver={startOver}
              inStock={availableSizes.includes(shownSize)}
            />
          )}
        </div>
      </div>
      <style>{`
        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>
    </div>
  );
}

// ─────────── Step 1 — height / weight / age / shopping for ───────────
const Step1 = ({
  heightUnit, setHeightUnit,
  heightCm, setHeightCm,
  heightFt, setHeightFt,
  heightIn, setHeightIn,
  weightUnit, setWeightUnit,
  weight, setWeight,
  age, setAge,
  shoppingFor, setShoppingFor,
  onContinue, valid,
}) => (
  <div data-testid="fms-step-1">
    <h2 id="fms-title" className="font-serif text-2xl leading-tight">
      Find your size for each of our products
    </h2>
    <p className="text-sm text-black/60 mt-2">
      First, a few questions to get to know you better.
    </p>

    <div className="mt-8 space-y-6">
      <Field label="Height*">
        {heightUnit === "cm" ? (
          <div className="flex">
            <input
              data-testid="fms-height-cm"
              type="number"
              inputMode="numeric"
              placeholder="175"
              value={heightCm}
              onChange={(e) => setHeightCm(e.target.value)}
              className="flex-1 border border-black/20 px-4 py-3 focus:outline-none focus:border-black text-sm"
            />
            <UnitToggle value={heightUnit} onChange={setHeightUnit} options={[["cm", "cm"], ["feet", "feet"]]} />
          </div>
        ) : (
          <div className="flex">
            <input
              data-testid="fms-height-ft"
              type="number"
              inputMode="numeric"
              placeholder="5"
              value={heightFt}
              onChange={(e) => setHeightFt(e.target.value)}
              className="flex-1 border border-black/20 px-4 py-3 focus:outline-none focus:border-black text-sm"
            />
            <input
              data-testid="fms-height-in"
              type="number"
              inputMode="numeric"
              placeholder="8"
              value={heightIn}
              onChange={(e) => setHeightIn(e.target.value)}
              className="flex-1 border border-l-0 border-black/20 px-4 py-3 focus:outline-none focus:border-black text-sm"
            />
            <UnitToggle value={heightUnit} onChange={setHeightUnit} options={[["cm", "cm"], ["feet", "feet"]]} />
          </div>
        )}
      </Field>

      <Field label="Weight*">
        <div className="flex">
          <input
            data-testid="fms-weight"
            type="number"
            inputMode="numeric"
            placeholder={weightUnit === "kg" ? "74" : "163"}
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            className="flex-1 border border-black/20 px-4 py-3 focus:outline-none focus:border-black text-sm"
          />
          <UnitToggle value={weightUnit} onChange={setWeightUnit} options={[["kg", "kg"], ["lbs", "lbs"]]} />
        </div>
      </Field>

      <Field label="Age*">
        <input
          data-testid="fms-age"
          type="number"
          inputMode="numeric"
          placeholder="27"
          value={age}
          onChange={(e) => setAge(e.target.value)}
          className="w-full border border-black/20 px-4 py-3 focus:outline-none focus:border-black text-sm"
        />
      </Field>

      <div>
        <p className="text-sm font-medium text-black">Shopping for</p>
        <div className="mt-3 grid grid-cols-2 gap-3">
          {["women", "men"].map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setShoppingFor(g)}
              data-testid={`fms-shopping-${g}`}
              className={`py-3 text-[11px] tracking-[0.2em] uppercase border transition-colors ${
                shoppingFor === g
                  ? "bg-black text-white border-black"
                  : "bg-white text-black border-black/25 hover:border-black"
              }`}
            >
              {g}
            </button>
          ))}
        </div>
      </div>
    </div>

    <button
      type="button"
      onClick={onContinue}
      disabled={!valid}
      data-testid="fms-step-1-continue"
      className="mt-10 w-full bg-black text-white text-[12px] tracking-[0.22em] uppercase py-4 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#c9a84c] hover:text-black transition-colors"
    >
      Continue
    </button>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="text-sm font-medium text-black">{label}</label>
    <div className="mt-2">{children}</div>
  </div>
);

const UnitToggle = ({ value, onChange, options }) => (
  <div className="flex">
    {options.map(([id, label]) => (
      <button
        key={id}
        type="button"
        onClick={() => onChange(id)}
        className={`px-4 py-3 text-[11px] tracking-[0.2em] uppercase border border-l-0 border-black/20 ${
          value === id ? "bg-black text-white border-black" : "bg-white text-black/60 hover:text-black"
        }`}
      >
        {label}
      </button>
    ))}
  </div>
);

// ─────────── Shape step (used for stomach / torso / thigh) ───────────
const ShapeStep = ({ testPrefix, title, subtitle, options, value, onChange, onContinue, dotIndex, iconRender, ctaLabel = "Continue" }) => (
  <div data-testid={`fms-step-${testPrefix}`}>
    <h2 id="fms-title" className="font-serif text-2xl leading-tight">{title}</h2>
    <p className="text-sm text-black/60 mt-2">{subtitle}</p>

    <div className="mt-8 grid grid-cols-3 gap-3">
      {options.map((o) => {
        const active = value === o.id;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            data-testid={`fms-${testPrefix}-${o.id}`}
            aria-pressed={active}
            className={`flex flex-col items-center border p-3 pb-4 transition-colors ${
              active ? "border-black ring-1 ring-black" : "border-black/20 hover:border-black/60"
            }`}
          >
            <div className="w-full aspect-[2/3] bg-white">{iconRender(o.id)}</div>
            <span className="mt-2 text-[11px] tracking-[0.18em] uppercase text-black">
              {o.label}
            </span>
          </button>
        );
      })}
    </div>

    {/* progress dots */}
    <div className="flex items-center justify-center gap-2 mt-8">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className={`w-1.5 h-1.5 rounded-full ${i === dotIndex ? "bg-black" : "bg-black/25"}`}
        />
      ))}
    </div>

    <button
      type="button"
      onClick={onContinue}
      disabled={!value}
      data-testid={`fms-${testPrefix}-continue`}
      className="mt-6 w-full bg-black text-white text-[12px] tracking-[0.22em] uppercase py-4 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#c9a84c] hover:text-black transition-colors"
    >
      {ctaLabel}
    </button>

    <button
      type="button"
      data-testid={`fms-${testPrefix}-scan`}
      className="mt-3 w-full border border-black/20 py-3 text-[11px] tracking-[0.22em] uppercase inline-flex items-center justify-center gap-2 hover:border-black transition-colors"
      onClick={() => alert("Body Scan is coming soon.")}
    >
      <Camera size={14} strokeWidth={1.5} />
      For more accuracy, try the body scan
    </button>
  </div>
);

// ─────────── Result step ───────────
const ResultStep = ({ shownSize, idealSize, fitVariant, setFitVariant, onAdd, onStartOver, inStock }) => (
  <div data-testid="fms-step-result">
    <h2 id="fms-title" className="font-serif text-2xl leading-tight">Recommended size</h2>

    <div className="mt-10 flex flex-col items-center">
      <span
        data-testid="fms-recommended-size"
        className="font-serif text-[112px] leading-none text-black"
      >
        {shownSize}
      </span>
      <p className="mt-3 text-sm text-emerald-600 inline-flex items-center gap-1.5">
        <span className="inline-block w-4 h-4 rounded-full border border-emerald-600 flex items-center justify-center text-[10px]">✓</span>
        {fitVariant === "ideal" ? "Perfect size" : fitVariant === "slimmer" ? "Slimmer fit" : "Looser fit"}
      </p>
    </div>

    <div className="mt-8 grid grid-cols-3 border border-black/15" data-testid="fms-fit-tabs">
      {[
        ["slimmer", "Slimmer fit"],
        ["ideal", "Ideal"],
        ["looser", "Looser fit"],
      ].map(([id, label]) => {
        const active = fitVariant === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => setFitVariant(id)}
            data-testid={`fms-fit-${id}`}
            className={`py-3 text-[11px] tracking-[0.2em] uppercase transition-colors ${
              active ? "text-emerald-700 ring-1 ring-emerald-600 -m-px" : "text-black/70 hover:text-black"
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>

    <p className="mt-6 text-sm text-black/65 leading-relaxed">
      Thousands of customers with a similar body shape to yours were very
      satisfied with purchasing this item in size {idealSize}.
    </p>

    <button
      type="button"
      onClick={onAdd}
      disabled={!inStock}
      data-testid="fms-add-to-cart"
      className="mt-6 w-full bg-black text-white text-[12px] tracking-[0.22em] uppercase py-4 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#c9a84c] hover:text-black transition-colors"
    >
      {inStock ? `Add size ${shownSize} to cart` : `Size ${shownSize} unavailable`}
    </button>

    <button
      type="button"
      onClick={onStartOver}
      data-testid="fms-start-over"
      className="mt-4 w-full text-[11px] tracking-[0.22em] uppercase underline underline-offset-4 hover:text-[#c9a84c]"
    >
      Start over
    </button>
  </div>
);
